import { useEffect, useState } from 'react';
import { downloadExport, api, type MeResponse, type SchoolYearRow } from '../api';
import { messageFor } from '../App';

/**
 * Export a school year, or an arbitrary window.
 *
 * A year is the unit an auditor asks about, so it is the default and it is named rather than
 * spelled out as two dates. Exporting by year also carries the statute that applied at the
 * time: a family that has since moved must not have an old year re-measured against a law
 * that was not in force then.
 */
export function ExportView({ me }: { me: MeResponse }) {
  const [years, setYears] = useState<SchoolYearRow[]>([]);
  const [year, setYear] = useState<string>('');
  const [childId, setChildId] = useState('');
  const [custom, setCustom] = useState(false);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState(new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  useEffect(() => {
    void api
      .years()
      .then((res) => {
        setYears(res.years);
        const current = res.years.find((y) => y.current) ?? res.years[0];
        if (current) {
          setYear(current.from);
          setFrom(current.from);
        }
      })
      .catch((err) => setError(messageFor(err)));
  }, []);

  const selected = years.find((y) => y.from === year);

  async function run(format: 'pdf' | 'xlsx') {
    setBusy(true);
    setError(undefined);
    try {
      await downloadExport({
        format,
        ...(custom ? { from, to } : { year }),
        ...(childId ? { childId } : {}),
      });
    } catch (err) {
      setError(messageFor(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {error && <div className="notice error">{error}</div>}

      <div className="panel">
        <h2>Export the log</h2>
        <p className="sub">
          The PDF is a readable record with hour totals on the cover. The spreadsheet has the
          same data with live formulas, so totals recalculate if anything is changed.
        </p>

        <div className="row">
          <div>
            <label htmlFor="xyear">School year</label>
            <select
              id="xyear"
              value={custom ? 'custom' : year}
              onChange={(e) => {
                if (e.target.value === 'custom') {
                  setCustom(true);
                } else {
                  setCustom(false);
                  setYear(e.target.value);
                }
              }}
            >
              {years.map((y) => (
                <option key={y.from} value={y.from}>
                  {y.label}
                  {y.current ? ' (current)' : ''} — {y.totalHours.toFixed(0)} hrs
                </option>
              ))}
              <option value="custom">Custom date range…</option>
            </select>
          </div>
          <div>
            <label htmlFor="xchild">Student</label>
            <select id="xchild" value={childId} onChange={(e) => setChildId(e.target.value)}>
              <option value="">All students</option>
              {(me.children ?? []).map((child) => (
                <option key={child.childId} value={child.childId}>{child.name}</option>
              ))}
            </select>
          </div>
        </div>

        {custom && (
          <div className="row" style={{ marginTop: 10 }}>
            <div>
              <label htmlFor="xfrom">From</label>
              <input id="xfrom" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            </div>
            <div>
              <label htmlFor="xto">To</label>
              <input id="xto" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
            </div>
          </div>
        )}

        <p className="muted" style={{ marginTop: 12 }}>
          Hour requirements apply per student, so pick one student when demonstrating that a
          particular child met them.
        </p>

        <button className="primary" disabled={busy} onClick={() => void run('pdf')}>
          {busy ? 'Preparing…' : 'Download PDF'}
        </button>{' '}
        <button disabled={busy} onClick={() => void run('xlsx')}>Download Excel</button>
      </div>

      {!custom && selected && selected.children.length > 0 && (
        <div className="panel">
          <h2>{selected.label}</h2>
          <p className="sub">
            {selected.from} to {selected.to} · {selected.eventCount} entries
          </p>
          <table>
            <thead>
              <tr><th>Student</th><th>Hours</th><th>Measured against</th><th>Progress</th></tr>
            </thead>
            <tbody>
              {selected.children.map((child) => (
                <tr key={child.childId}>
                  <td>
                    <span className="swatch" style={{ background: child.color, borderColor: child.color }} />{' '}
                    {child.name}
                    {!child.active && <span className="muted"> (retired)</span>}
                  </td>
                  <td>{(child.totals.totalMinutes / 60).toFixed(1)}</td>
                  <td className="muted">
                    {me.jurisdictions?.find((j) => j.id === child.jurisdictionId)?.label ??
                      child.jurisdictionId}
                  </td>
                  <td className="muted">
                    {child.progress.length === 0
                      ? 'no hour requirement'
                      : child.progress
                          .map((p) => `${p.label} ${p.loggedHours.toFixed(0)}/${p.requiredHours}`)
                          .join(' · ')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {selected.children.some((c) => c.unverifiedCount > 0) && (
            <p className="muted" style={{ marginTop: 10 }}>
              Some entries in this year are not yet confirmed by a parent. They are marked in
              the export.
            </p>
          )}
        </div>
      )}
    </>
  );
}
